import { RefreshCw, ServerCrash, WifiOff } from 'lucide-react'
import { useEffect, useState } from 'react'

import { HEALTH_TIMINGS, formatSpan, selectConnectionNeedsAttention, selectMaintenanceActive, offlineWindowText, useLinkOwnedByScreen, type ConnectionState } from '@krp/client-runtime'
import { Button, DiagnosticCopy, ModalFrame } from '@krp/ui-kit'

import { connectionCheckRequested, selectConnection } from './connection'
import { useAppDispatch, useAppSelector } from './hooks'

/** Как часто обновлять обратный отсчёт до следующей проверки. */
const COUNTDOWN_TICK_MS = 500

export const OVERLAY_TITLE = 'Восстанавливаем соединение с сервером'

/** Текст для администратора: вид, код и номер запроса последней ошибки связи. */
function reportOf(connection: ConnectionState): string {
  const error = connection.lastError
  return [
    `Связь с сервером: ${connection.status === 'offline' ? 'нет сети на устройстве' : 'сервер не отвечает'}`,
    ...(connection.since !== null ? [`Без связи с: ${new Date(connection.since).toISOString()}`] : []),
    `Проверок: ${connection.attempts}`,
    ...(error !== null
      ? [
          `Ошибка: ${error.message}`,
          `Вид: ${error.kind}; код: ${error.code}${error.status !== undefined ? `; HTTP ${error.status}` : ''}`,
          ...(error.correlationId !== null ? [`Номер запроса: ${error.correlationId}`] : []),
        ]
      : []),
  ].join('\n')
}

/**
 * Окно связи с сервером по центру экрана (решение владельца 23.10.2026): единственное место, где показан сбой связи, —
 * экраны и блоки внизу его не повторяют. Появляется, если связи нет дольше `HEALTH_TIMINGS.serverOverlayDelayMs` (единичный
 * сбой, который тут же прошёл, не мигает окном), и закрывается само, когда сервер ответил. Кнопки «Закрыть» нет: без сервера работать всё равно нельзя. Состояние различается значком и
 * текстом, а не только цветом (CLAUDE.md §7).
 *
 * Экран, который сам показывает связь (продажа и окно оплаты, RP-SALE-FAST, К-107), объявляет это на доске окна — пока он на экране,
 * это окно не рисуется: оно лежало бы поверх «Отменить» и «Свернуть».
 */
export function ConnectionOverlay() {
  const dispatch = useAppDispatch()
  const connection = useAppSelector(selectConnection)
  const { status, since, attempts, nextCheckAt, lastError } = connection
  const shownByScreen = useLinkOwnedByScreen()
  const needsAttention = selectConnectionNeedsAttention(connection)
  // RP-MNT: во время обновления потеря связи ожидаема — спокойный замок РМК объясняет это, красное окно не нужно.
  const maintenance = useAppSelector((state) => selectMaintenanceActive(state.maintenance))
  /** Когда окно приложения последний раз вернулось на экран: свёрнутое окно связь не проверяет, сбои за это время человек не видел. */
  const [visibleAt, setVisibleAt] = useState(() => Date.now())
  /** От какой точки отсчёта (позже из обрыва и возврата окна) задержка показа уже прошла. */
  const [shownFrom, setShownFrom] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const anchor = since === null ? null : Math.max(since, visibleAt)

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setVisibleAt(Date.now())
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  useEffect(() => {
    if (anchor === null) return undefined
    const timer = window.setTimeout(() => setShownFrom(anchor), Math.max(0, anchor + HEALTH_TIMINGS.serverOverlayDelayMs - Date.now()))
    return () => window.clearTimeout(timer)
  }, [anchor])

  const visible = !shownByScreen && !maintenance && needsAttention && anchor !== null && shownFrom === anchor

  // Обратный отсчёт до следующей проверки — только пока окно на экране.
  useEffect(() => {
    if (!visible) return undefined
    const tick = () => setNow(Date.now())
    const timer = window.setInterval(tick, COUNTDOWN_TICK_MS)
    const first = window.setTimeout(tick, 0)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(first)
    }
  }, [visible, nextCheckAt])

  if (!visible) return null

  const offline = status === 'offline'
  const secondsLeft = nextCheckAt === null ? null : Math.ceil((nextCheckAt - now) / 1000)
  const progress =
    secondsLeft === null || secondsLeft <= 0
      ? `Попытка ${Math.max(1, attempts)}: проверяем сервер…`
      : `Попытка ${attempts}, следующая — через ${secondsLeft} с.`

  return (
    <ModalFrame title={OVERLAY_TITLE} size="sm" covered={false}>
      <div className="connection-overlay" role="alert" aria-live="assertive" data-connection={status}>
        <p className="connection-overlay__lead">
          {offline ? <WifiOff size={20} aria-hidden="true" /> : <ServerCrash size={20} aria-hidden="true" />}
          <span>
            {offline
              ? `${offlineWindowText(formatSpan(since === null ? 0 : now - since))} ${progress}`
              : `Сервер не отвечает. Проверьте интернет. ${progress}`}
          </span>
        </p>
        <Button
          variant="primary"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          onClick={() => dispatch(connectionCheckRequested())}
          actionId="shell.connection-retry"
        >
          Повторить сейчас
        </Button>
        {lastError !== null && (
          <p className="connection-overlay__error">
            Последняя ошибка: {lastError.message} · код {lastError.code}
            {lastError.correlationId !== null ? ` · запрос ${lastError.correlationId}` : ''}
          </p>
        )}
        <DiagnosticCopy text={reportOf(connection)} label="Скопировать для администратора" />
      </div>
    </ModalFrame>
  )
}
